<template>
  <div class="calendar-root">
    <v-menu
      content-class="v-menu__content__min-width-fix"
      v-model="isOpen"
      transition="scale-transition"
      origin="top"
      :close-on-content-click="false"
      :min-width="0"
      offset-y
    >
      <template v-slot:activator="{ on }">
        <div
          class="date-selection-wrapper"
          :class="{ focused: textDateRange }"
          v-on="on"
        >
          <div class="label">{{ $t("baseFilters.datePicker.inputLabel") }}</div>
          <div class="selection pb-1">
            <v-chip
              v-if="textDateRange"
              class="font-weight-bold white--text"
              style="font-size: 13px"
              color="primary"
            >
              <span v-html="textDateRange"></span>
              <v-icon right @click.stop="clearInput"
                >fas fa-times-circle</v-icon
              >
            </v-chip>
          </div>
          <v-icon small>fas fa-caret-down</v-icon>
        </div>
      </template>

      <div
        class="calendar"
        :class="{
          'calendar-mobile ': isCompact,
          'calendar-right-to-left': isRighttoLeft
        }"
      >
        <v-row no-gutters align="center">
          <v-col cols="auto">
            <h2 class="calendar-title">{{ captions.title }}</h2>
          </v-col>
          <v-col
            v-if="hasDateToUse"
            :ml-3="!isSmallDevice"
            :mt-3="isSmallDevice"
            class="flex-grow-1 flex-shrink-1"
          >
            <v-radio-group
              v-model="dateToUse"
              class="ma-0 pa-0 radio-group"
              row
              hide-details
            >
              <v-radio
                v-for="(dateToUseItem, index) in dateToUseOptions"
                :key="index"
                :label="$t(`baseFilters.datePicker.dateToUse.${dateToUseItem}`)"
                :value="dateToUseItem"
              ></v-radio>
            </v-radio-group>
          </v-col>
        </v-row>
        <div class="calendar-dates-wrapper">
          <v-row justify-space-between no-gutters align="start">
            <v-col
              class="calendar_month_left"
              :class="{ 'calendar-left-mobile': isCompact }"
            >
              <div class="months-text">
                <v-icon
                  class="float-left"
                  color="primary"
                  @click="goPrevMonth"
                  size="18"
                  >fas fa-long-arrow-alt-left</v-icon
                >
                <v-icon
                  v-if="isCompact"
                  class="float-right"
                  color="primary"
                  @click="goNextMonth"
                  size="18"
                  >fas fa-long-arrow-alt-right</v-icon
                >
                {{ monthsLocale[activeMonthStart] + " " + activeYearStart }}
              </div>
              <ul :class="s.daysWeeks">
                <li v-for="item in shortDaysLocale" :key="item">{{ item }}</li>
              </ul>
              <ul class="mb-1" v-for="r in 6" :class="[s.days]" :key="r">
                <li
                  :class="[
                    {
                      [s.daysSelected]: isDateSelected(
                        r,
                        i,
                        'first',
                        startMonthDay,
                        endMonthDate
                      ),
                      [s.startDate]: isStartDate(
                        r,
                        i,
                        'first',
                        startMonthDay,
                        endMonthDate
                      ),
                      [s.endDate]: isEndDate(
                        r,
                        i,
                        'first',
                        startMonthDay,
                        endMonthDate
                      ),
                      [s.daysInRange]: isDateInRange(
                        r,
                        i,
                        'first',
                        startMonthDay,
                        endMonthDate
                      ),
                      [s.dateDisabled]: isDateDisabled(
                        r,
                        i,
                        startMonthDay,
                        endMonthDate
                      )
                    }
                  ]"
                  v-for="i in numOfDays"
                  :key="i"
                  v-html="getDayCell(r, i, startMonthDay, endMonthDate)"
                  @click="selectFirstItem(r, i)"
                ></li>
              </ul>
            </v-col>
            <v-col class="calendar_month_right" v-if="!isCompact">
              <div class="months-text">
                {{ monthsLocale[startNextActiveMonth] + " " + activeYearEnd }}
                <v-icon
                  class="float-right"
                  color="primary"
                  @click="goNextMonth"
                  size="18"
                  >fas fa-long-arrow-alt-right</v-icon
                >
              </div>
              <ul :class="s.daysWeeks">
                <li v-for="item in shortDaysLocale" :key="item">{{ item }}</li>
              </ul>
              <ul class="mb-1" v-for="r in 6" :class="[s.days]" :key="r">
                <li
                  :class="[
                    {
                      [s.daysSelected]: isDateSelected(
                        r,
                        i,
                        'second',
                        startNextMonthDay,
                        endNextMonthDate
                      ),
                      [s.startDate]: isStartDate(
                        r,
                        i,
                        'second',
                        startNextMonthDay,
                        endNextMonthDate
                      ),
                      [s.endDate]: isEndDate(
                        r,
                        i,
                        'second',
                        startNextMonthDay,
                        endNextMonthDate
                      ),
                      [s.daysInRange]: isDateInRange(
                        r,
                        i,
                        'second',
                        startNextMonthDay,
                        endNextMonthDate
                      ),
                      [s.dateDisabled]: isDateDisabled(
                        r,
                        i,
                        startNextMonthDay,
                        endNextMonthDate
                      )
                    }
                  ]"
                  v-for="i in numOfDays"
                  :key="i"
                  v-html="getDayCell(r, i, startNextMonthDay, endNextMonthDate)"
                  @click="selectSecondItem(r, i)"
                ></li>
              </ul>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-spacer></v-spacer>
            <v-col class="flex-grow-0 flex-shrink-1">
              <v-btn
                class="ma-0 caption font-weight-bold"
                color="primary"
                @click="applyRange()"
                small
                rounded
                >{{ captions.ok_button }}</v-btn
              >
            </v-col>
          </v-row>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<script>
import dayjs from "dayjs";

const defaultConfig = {};
const defaultI18n = "EN";
const availableMonths = {
  EN: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ],
  ID: [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember"
  ]
};

const availableShortDays = {
  EN: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  ID: ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"]
};

const defaultCaptions = {
  title: "Choose Dates",
  ok_button: "Apply"
};

const defaultStyle = {
  daysWeeks: "calendar_weeks",
  days: "calendar_days",
  daysSelected: "calendar_days_selected",
  startDate: "calendar_days-start",
  endDate: "calendar_days-end",
  daysInRange: "calendar_days_in-range",
  firstDate: "calendar_month_left",
  secondDate: "calendar_month_right",
  dateDisabled: "calendar_days--disabled"
};

export default {
  name: "vue-rangedate-picker",
  props: {
    configs: {
      type: Object,
      default: () => defaultConfig
    },
    i18n: {
      type: String,
      default: defaultI18n
    },
    months: {
      type: Array,
      default: () => null
    },
    shortDays: {
      type: Array,
      default: () => null
    },
    // options for captions are: title, ok_button
    captions: {
      type: Object,
      default: () => defaultCaptions
    },
    format: {
      type: String,
      default: "DD MMM YYYY"
    },
    styles: {
      type: Object,
      default: () => {}
    },
    initRange: {
      type: Object,
      default: () => null
    },
    startActiveMonth: {
      type: Number,
      default: new Date().getMonth()
    },
    startActiveYear: {
      type: Number,
      default: new Date().getFullYear()
    },
    compact: {
      type: Boolean,
      default: false
    },
    righttoleft: {
      type: Boolean,
      default: false
    },
    hasDateToUse: {
      type: Boolean,
      default: false
    },
    initDateToUse: {
      type: String,
      default: ""
    },
    dateToUseOptions: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dateRange: {},
      numOfDays: 7,
      isFirstChoice: true,
      isOpen: false,
      activeMonthStart: this.startActiveMonth,
      activeYearStart: this.startActiveYear,
      textDateRange: "",
      dateToUse: ""
    };
  },
  created() {
    if (this.initRange) {
      this.dateRange.start = this.initRange.start;
      this.dateRange.end = this.initRange.end;
      if (this.hasDateToUse) this.dateToUse = this.initDateToUse;
      this.textDateRange = this.buildTextDateRange();
    }
  },
  mounted() {},
  watch: {
    initRange() {
      if (this.initRange) {
        this.dateRange.start = this.initRange.start;
        this.dateRange.end = this.initRange.end;
      }
      if (this.hasDateToUse) this.dateToUse = this.initDateToUse;
      this.textDateRange = this.buildTextDateRange();
    },
    isOpen() {
      if (this.isOpen) this.isFirstChoice = true;
      this.toggleCalendar();
    },
    dateToUse() {
      this.textDateRange = this.buildTextDateRange();
    }
  },
  computed: {
    isSmallDevice() {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    activeYearEnd() {
      return this.activeMonthStart === 11
        ? this.activeYearStart + 1
        : this.activeYearStart;
    },
    monthsLocale() {
      return this.months || availableMonths[this.i18n];
    },
    shortDaysLocale() {
      return this.shortDays || availableShortDays[this.i18n];
    },
    s() {
      return Object.assign({}, defaultStyle, this.style);
    },
    startMonthDay() {
      return dayjs()
        .utc()
        .year(this.activeYearStart)
        .month(this.activeMonthStart)
        .startOf("month")
        .day();
    },
    startNextMonthDay() {
      if (this.activeMonthStart === 11) {
        return dayjs()
          .utc()
          .year(this.activeYearStart)
          .month(this.startNextActiveMonth)
          .add(1, "year")
          .startOf("month")
          .day();
      } else {
        return dayjs()
          .utc()
          .year(this.activeYearStart)
          .month(this.startNextActiveMonth)
          .startOf("month")
          .day();
      }
    },
    endMonthDate() {
      if (this.activeYearStart === this.activeYearEnd)
        return dayjs()
          .utc()
          .year(this.activeYearEnd)
          .month(this.activeMonthStart)
          .endOf("month")
          .date();
      else
        return dayjs()
          .utc()
          .year(this.activeYearStart)
          .month(this.activeMonthStart)
          .endOf("month")
          .date();
    },
    endNextMonthDate() {
      if (this.activeYearStart === this.activeYearEnd)
        return dayjs()
          .utc()
          .year(this.activeYearEnd)
          .month(this.activeMonthStart)
          .add(1, "month")
          .endOf("month")
          .date();
      else
        return dayjs()
          .utc()
          .year(this.activeYearStart)
          .month(this.activeMonthStart)
          .add(1, "month")
          .endOf("month")
          .date();
    },
    startNextActiveMonth() {
      return this.activeMonthStart >= 11 ? 0 : this.activeMonthStart + 1;
    },
    isCompact() {
      return this.compact || this.isSmallDevice;
    },
    isRighttoLeft() {
      return this.righttoleft && !this.isSmallDevice;
    }
  },
  methods: {
    buildTextDateRange() {
      let start = "";
      let end = "";
      if (this.dateRange.start) {
        start = this.getDateString(this.dateRange.start);
      }
      if (this.dateRange.end) end = this.getDateString(this.dateRange.end);

      let dateToUseText = this.hasDateToUse
        ? "<p class='p-dateToUse'>" +
          this.$t(`baseFilters.datePicker.dateToUse.${this.dateToUse}`) +
          "</p>"
        : "";

      return start || end
        ? this.hasDateToUse
          ? "<p class='p-datesWithDateToUse'>" +
            start +
            " - " +
            end +
            "</p>" +
            dateToUseText
          : "<p class='p-onlyDates'>" + start + " - " + end + "</p>"
        : "";
    },
    clearInput() {
      this.dateRange = {};
      this.$emit("cleardaterange");
      if (this.isOpen) {
        this.toggleCalendar();
      }
    },
    toggleCalendar() {
      if (this.isOpen) {
        if (this.initRange) {
          this.dateRange.start = this.initRange.start;
          this.dateRange.end = this.initRange.end;

          if (this.dateRange.start) {
            this.activeMonthStart = dayjs(this.dateRange.start).utc().month();
            this.activeYearStart = dayjs(this.dateRange.start).utc().year();
          } else {
            this.activeMonthStart = dayjs(this.dateRange.end).utc().month();
            this.activeYearStart = dayjs(this.dateRange.end).utc().year();
          }
        } else {
          this.activeMonthStart = dayjs().utc().month();
          this.activeYearStart = dayjs().utc().year();
          this.dateRange = {};
        }

        if (this.hasDateToUse) this.dateToUse = this.initDateToUse;
      } else {
        if (this.initRange) {
          this.dateRange.start = this.initRange.start;
          this.dateRange.end = this.initRange.end;
        } else {
          this.dateRange = {};
        }
        if (this.hasDateToUse) this.dateToUse = this.initDateToUse;
      }
      this.textDateRange = this.buildTextDateRange();

      return;
    },
    getDateString(date) {
      if (!date) {
        return "";
      }
      return dayjs(date).utc().format("DD/MM/YY");
    },
    getDayIndexInMonth(r, i, startMonthDay) {
      const date = this.numOfDays * (r - 1) + i;
      return date - startMonthDay;
    },
    getDayCell(r, i, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      // bound by > 0 and < last day of month
      return result > 0 && result <= endMonthDate ? result : "&nbsp;";
    },
    getNewDateRange(result, activeMonth, activeYear) {
      const newData = {};
      let key = "start";
      if (!this.isFirstChoice) {
        key = "end";
      } else {
        newData["end"] = null;
      }
      const resultDate = new Date(Date.UTC(activeYear, activeMonth, result));
      if (!this.isFirstChoice && resultDate < this.dateRange.start) {
        this.isFirstChoice = true;
        return {
          start: resultDate,
          end: this.dateRange.start
        };
      }
      this.isFirstChoice = !this.isFirstChoice;
      newData[key] = resultDate;
      return newData;
    },
    selectFirstItem(r, i) {
      const result = this.getDayIndexInMonth(r, i, this.startMonthDay);
      this.dateRange = Object.assign(
        {},
        this.dateRange,
        this.getNewDateRange(
          result,
          this.activeMonthStart,
          this.activeYearStart
        )
      );

      this.textDateRange = this.buildTextDateRange();
    },
    selectSecondItem(r, i) {
      const result = this.getDayIndexInMonth(r, i, this.startNextMonthDay);
      this.dateRange = Object.assign(
        {},
        this.dateRange,
        this.getNewDateRange(
          result,
          this.startNextActiveMonth,
          this.activeYearEnd
        )
      );
      this.textDateRange = this.buildTextDateRange();
    },
    isDateSelected(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      if (result < 1 || result > endMonthDate) return false;

      let currDate = null;
      if (key === "first") {
        currDate = new Date(
          Date.UTC(this.activeYearStart, this.activeMonthStart, result)
        );
      } else {
        currDate = new Date(
          Date.UTC(this.activeYearEnd, this.startNextActiveMonth, result)
        );
      }
      return (
        (this.dateRange.start &&
          this.dateRange.start.getTime() === currDate.getTime()) ||
        (this.dateRange.end &&
          this.dateRange.end.getTime() === currDate.getTime())
      );
    },
    isStartDate(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      if (result < 1 || result > endMonthDate) return false;

      let currDate = null;
      if (key === "first") {
        currDate = new Date(
          Date.UTC(this.activeYearStart, this.activeMonthStart, result)
        );
      } else {
        currDate = new Date(
          Date.UTC(this.activeYearEnd, this.startNextActiveMonth, result)
        );
      }

      return (
        this.dateRange.start &&
        this.dateRange.end &&
        this.dateRange.start.getTime() === currDate.getTime() &&
        this.dateRange.start.getTime() !== this.dateRange.end.getTime()
      );
    },
    isEndDate(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      if (result < 1 || result > endMonthDate) return false;

      let currDate = null;
      if (key === "first") {
        currDate = new Date(
          Date.UTC(this.activeYearStart, this.activeMonthStart, result)
        );
      } else {
        currDate = new Date(
          Date.UTC(this.activeYearEnd, this.startNextActiveMonth, result)
        );
      }

      return (
        this.dateRange.end &&
        this.dateRange.start &&
        this.dateRange.end.getTime() === currDate.getTime() &&
        this.dateRange.start.getTime() !== this.dateRange.end.getTime()
      );
    },
    isDateInRange(r, i, key, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      if (result < 1 || result > endMonthDate) return false;

      let currDate = null;
      if (key === "first") {
        currDate = new Date(
          Date.UTC(this.activeYearStart, this.activeMonthStart, result)
        );
      } else {
        currDate = new Date(
          Date.UTC(this.activeYearEnd, this.startNextActiveMonth, result)
        );
      }
      return (
        this.dateRange.start &&
        this.dateRange.start.getTime() < currDate.getTime() &&
        this.dateRange.end &&
        this.dateRange.end.getTime() > currDate.getTime()
      );
    },
    isDateDisabled(r, i, startMonthDay, endMonthDate) {
      const result = this.getDayIndexInMonth(r, i, startMonthDay);
      // bound by > 0 and < last day of month
      return !(result > 0 && result <= endMonthDate);
    },
    goPrevMonth() {
      const prevMonth = new Date(
        Date.UTC(this.activeYearStart, this.activeMonthStart, 0)
      );
      this.activeMonthStart = prevMonth.getMonth();
      this.activeYearStart = prevMonth.getFullYear();
    },
    goNextMonth() {
      const nextMonth = new Date(
        Date.UTC(this.activeYearEnd, this.startNextActiveMonth, 1)
      );
      this.activeMonthStart = nextMonth.getMonth();
      this.activeYearStart = nextMonth.getFullYear();
    },
    applyRange() {
      if (!this.dateRange.end) {
        this.dateRange.end = this.dateRange.start;
        this.isFirstChoice = true;
      }
      this.$emit("applyrange", this.dateRange, this.dateToUse);
      this.isOpen = false;
    }
  }
};
</script>

<style scoped>
.input-date {
  font-size: 14px;
}

.months-text {
  text-align: center;
  font-weight: bold;
}
.calendar-root,
.calendar-title {
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}
.calendar {
  padding: 16px;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  width: 550px;
  font-size: 12px;
  box-shadow: -3px 4px 12px -1px #ccc;
  background: #fff;
  z-index: 1;
  padding-bottom: 14px;
}
.calendar-head h2 {
  padding: 20px 0 0 20px;
  margin: 0;
}
.calendar ul {
  list-style-type: none;
}
.calendar-left-mobile {
  width: 100% !important;
}
.calendar_month_left {
  padding-right: 20px;
}
.calendar-mobile .calendar_month_left {
  padding-right: unset;
}
.calendar_month_right {
  padding-left: 20px;
}
.calendar_weeks {
  margin: 0;
  padding: 10px 0;
  width: auto;
}
.calendar_weeks li {
  display: inline-block;
  width: 14.28%;
  color: #999;
  text-align: center;
}
.calendar_days {
  margin: 0;
  padding: 0;
}
.calendar_days li {
  display: inline-block;
  width: 14.28%;
  color: #333;
  text-align: center;
  cursor: pointer;
  line-height: 2em;
  border-radius: 28px;
}
.calendar_days li:hover {
  background: #eee;
  color: #000;
}
li.calendar_days--disabled {
  pointer-events: none;
}
li.calendar_days_selected {
  background: var(--primary);
  color: #fff;
}
li.calendar_days_selected:hover {
  background: #9fc6d8;
  color: #fff;
}
li.calendar_days-start {
  border-radius: 28px 0 0 28px;
}
li.calendar_days-end {
  border-radius: 0 28px 28px 0;
}
li.calendar_days_in-range {
  background: var(--secondary);
  color: #fff;
  border-radius: unset;
}
li.calendar_days_in-range:hover {
  background: #9fc6d8;
  color: #fff;
}
.calendar-mobile {
  width: 260px;
}
.calendar-switch >>> .v-input {
  width: 150px;
  margin-bottom: 5px;
}
.radio-group >>> .v-icon {
  font-size: 20px;
}
.radio-group >>> .v-label {
  color: black;
  font-weight: bold;
  font-size: 13px;
}
.calendar-dates-wrapper {
  margin-top: 20px;
}
.v-menu__content__min-width-fix {
  min-width: auto !important;
  margin-left: -100px;
}
@media screen and (max-width: 959px) {
  .v-menu__content__min-width-fix {
    margin-left: unset;
  }
}

/* I N P U T */

.date-selection-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.date-selection-wrapper::after {
  content: "";
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  border-bottom: 1px solid grey;
}

.date-selection-wrapper > .selection {
  min-height: unset;
  width: 100%;
}

.date-selection-wrapper.focused > .selection {
  min-height: 25px;
}

.date-selection-wrapper > .label {
  position: relative;
  top: -4px;
  left: 0;
  font-size: 15px;
  transition: all 0.6 ease;
  color: rgba(0, 0, 0, 0.4);
}

.date-selection-wrapper.focused > .label {
  transform: scale(0.8);
  flex-basis: 100%;
  top: 4px;
  left: -20px;
  color: var(--primary);
}

.date-selection-wrapper .v-chip {
  max-width: 80% !important;
}
.date-selection-wrapper .v-chip >>> .p-dateToUse {
  margin-bottom: 0px !important;
  max-height: 20px;
}
.date-selection-wrapper .v-chip >>> .p-datesWithDateToUse {
  margin-bottom: 0px !important;
  max-height: 15px;
}
.date-selection-wrapper .v-chip >>> .p-onlyDates {
  margin-bottom: 0px !important;
}
.date-selection-wrapper .v-chip >>> .v-icon {
  color: white;
  opacity: 0.7;
  font-size: 20px;
  margin-right: 0px;
}
.date-selection-wrapper .v-chip >>> .v-icon:hover {
  opacity: 1;
}
.date-selection-wrapper > .v-icon {
  position: absolute;
  right: 10px;
  bottom: 3px;
  font-size: 24px !important;
}
</style>
